
/**
 * The publish flow. AC-5.2, prototype screenshot b-publish.
 *
 * Composes a blank, a design, a placement and a method into a
 * `published_products` row, previewed live in the same mockup component the
 * storefront uses. Cost is suggested from the pricing engine so the margin is
 * visible while the retail price is being set, rather than discovered later on
 * the dashboard.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GarmentMockup } from '@/components/garment-mockup';
import { Button, Callout, Field, inputClass } from '@/components/ui';
import { methodRowToEngine } from '@/lib/data/seed';
import { formatCents } from '@/lib/money';
import { computeEstimate } from '@/lib/pricing/engine';
import type { Brand, PrintMethodRow, Product, ReadyDesign } from '@/lib/types';

export function PublishFlow({
  brands,
  blanks,
  designs,
  methods,
}: {
  brands: Brand[];
  blanks: Product[];
  designs: ReadyDesign[];
  methods: PrintMethodRow[];
}) {
  const [blankId, setBlankId] = useState(blanks[0]?.id ?? '');
  const [brandId, setBrandId] = useState(brands[0]?.id ?? '');
  const [designUrl, setDesignUrl] = useState(designs[0]?.image_url ?? '');
  const [methodKey, setMethodKey] = useState(methods[0]?.key ?? '');
  const [colourIndex, setColourIndex] = useState(0);
  const [colours, setColours] = useState(1);
  const [scale, setScale] = useState(60);
  const [retail, setRetail] = useState('39.95');
  const [title, setTitle] = useState('');
  const [published, setPublished] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /**
   * This writes a row now. R-269.
   *
   * It used to call `setPublished(...)` and nothing else: the button showed
   * "ready to publish" and the product was never in the shop. The message even
   * said "when Supabase is connected", written before it was, and left there
   * for a month after.
   */
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/published', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          product_id: blank?.id,
          brand_id: brandId,
          title: title || blank?.name,
          type: blank?.type,
          color_name: colour?.name,
          color_hex: colour?.hex,
          design_url: designUrl,
          method_key: methodKey,
          colours,
          scale,
          base_cost_cents: cost,
          retail_price_cents: retailCents,
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not publish it.');
      return body?.message ?? 'Published.';
    },
    onSuccess: (message) => {
      setFailed(null);
      setPublished(message);
      void qc.invalidateQueries({ queryKey: ['admin', 'published'] });
    },
    onError: (e: Error) => { setPublished(null); setFailed(e.message); },
  });

  const blank = blanks.find((b) => b.id === blankId) ?? blanks[0];
  const method = methods.find((m) => m.key === methodKey) ?? methods[0];
  const colour = blank?.colors[colourIndex] ?? blank?.colors[0];

  // Unit cost at qty 1, which is what a print-on-demand line actually costs.
  const cost = useMemo(() => {
    if (!blank || !method) return 0;
    const est = computeEstimate(blank.base_price_cents / 100, methodRowToEngine(method), {
      qty: 1,
      colours,
      locations: ['front'],
    });
    return Math.round(est.unit_price * 100);
  }, [blank, method, colours]);

  const retailCents = Math.round((Number(retail) || 0) * 100);
  const margin = retailCents - cost;
  const marginPct = retailCents > 0 ? Math.round((margin / retailCents) * 100) : 0;

  if (!blank || !method || !colour) return null;

  return (
    <section className="rounded-[10px] border border-line bg-panel p-5">
      <h2 className="text-lg font-bold tracking-[-0.02em]">Publish a product</h2>
      <p className="mt-1 text-[15px] text-muted">
        Compose a blank, a design and a decoration into a storefront listing.
      </p>

      {published && (
        <div className="mt-5">
          <Callout tone="ok" title={published}>
            It is in the published list below, and in the shop. Take it out again from
            that list if it is not ready.
          </Callout>
        </div>
      )}

      {failed && (
        <div className="mt-5">
          <Callout tone="danger" title={failed} />
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
        <div>
          <div className="rounded-[10px] border border-line bg-raised p-3">
            <GarmentMockup
              type={blank.type}
              colorHex={colour.hex}
              designUrl={designUrl}
              placement={{ pos_x: 50, pos_y: blank.type === 'cap' ? 50 : 48, scale }}
              showZone
              label={`Preview of ${title || blank.name} in ${colour.name}`}
              className="aspect-square w-full"
            />
          </div>

          <dl className="mt-4 space-y-2 text-[15px]">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Unit cost</dt>
              <dd className="font-mono font-semibold">{formatCents(cost)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Retail</dt>
              <dd className="font-mono font-semibold">{formatCents(retailCents)}</dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-line pt-2">
              <dt className="font-semibold">Margin</dt>
              <dd className="font-mono font-bold">
                {formatCents(margin)}
                <span className="ml-1.5 text-[13px] font-normal text-muted">
                  {marginPct}%
                </span>
              </dd>
            </div>
          </dl>

          {margin <= 0 && (
            <div className="mt-3">
              <Callout tone="danger" title="This sells at a loss">
                Retail is at or below the cost to produce one unit.
              </Callout>
            </div>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Title" htmlFor="pub-title">
            <input
              id="pub-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={blank.name}
              className={inputClass}
            />
          </Field>

          <Field label="Brand" htmlFor="pub-brand">
            <select
              id="pub-brand"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              className={`${inputClass} h-[50px] py-0`}
            >
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Blank" htmlFor="pub-blank">
            <select
              id="pub-blank"
              value={blankId}
              onChange={(e) => {
                setBlankId(e.target.value);
                setColourIndex(0);
              }}
              className={`${inputClass} h-[50px] py-0`}
            >
              {blanks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Colour" htmlFor="pub-colour">
            <select
              id="pub-colour"
              value={colourIndex}
              onChange={(e) => setColourIndex(Number(e.target.value))}
              className={`${inputClass} h-[50px] py-0`}
            >
              {blank.colors.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Decoration" htmlFor="pub-method">
            <select
              id="pub-method"
              value={methodKey}
              onChange={(e) => setMethodKey(e.target.value)}
              className={`${inputClass} h-[50px] py-0`}
            >
              {methods.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Design colours" htmlFor="pub-colours">
            <input
              id="pub-colours"
              inputMode="numeric"
              value={colours}
              onChange={(e) => setColours(Math.max(1, Number(e.target.value) || 1))}
              className={`${inputClass} font-mono`}
            />
          </Field>

          <div className="sm:col-span-2">
            <p className="text-sm font-semibold">Design</p>
            <div className="mt-2.5 flex flex-wrap gap-3">
              {designs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDesignUrl(d.image_url)}
                  aria-pressed={designUrl === d.image_url}
                  className={
                    'checkerboard size-[76px] rounded-[10px] border-2 p-1.5 ' +
                    (designUrl === d.image_url
                      ? 'border-brand'
                      : 'border-line-strong hover:border-ink/40')
                  }
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={d.image_url} alt={d.name} className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="pub-scale" className="text-sm font-semibold">
              Design size
            </label>
            <div className="mt-2.5 flex items-center gap-4">
              <input
                id="pub-scale"
                type="range"
                min={15}
                max={100}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-line-strong accent-[var(--color-brand-strong)]"
              />
              <output className="w-12 text-right font-mono text-sm font-bold">{scale}%</output>
            </div>
          </div>

          <Field label="Retail price (AUD, incl GST)" htmlFor="pub-retail">
            <input
              id="pub-retail"
              inputMode="decimal"
              value={retail}
              onChange={(e) => setRetail(e.target.value)}
              className={`${inputClass} font-mono`}
            />
          </Field>

          <div className="flex items-end">
            <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
              {publish.isPending ? 'Publishing' : 'Publish product'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
